import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Calendar, Building2 } from 'lucide-react';
import { getPressReleases, getCategories, getTags } from '@/lib/api';
import SEO from '@/components/SEO';

// Helper function to strip HTML tags from text
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').substring(0, 200);
};

const Newsroom = () => {
  const [pressReleases, setPressReleases] = useState([]);
  const [filteredReleases, setFilteredReleases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [releasesRes, categoriesRes, tagsRes] = await Promise.all([
          getPressReleases(),
          getCategories(),
          getTags()
        ]);
        setPressReleases(releasesRes.data);
        setFilteredReleases(releasesRes.data);
        setCategories(categoriesRes.data.categories || []);
        setTags(tagsRes.data.tags || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = pressReleases;

    if (searchQuery) {
      filtered = filtered.filter(pr =>
        pr.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pr.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pr.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(pr => pr.categories.includes(selectedCategory));
    }

    if (selectedTag) {
      filtered = filtered.filter(pr => pr.tags.includes(selectedTag));
    }

    setFilteredReleases(filtered);
  }, [searchQuery, selectedCategory, selectedTag, pressReleases]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading press releases...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Newsroom"
        description="Browse the latest press releases and company news distributed through Ixoraly. Stay updated with announcements from businesses worldwide."
        path="/newsroom"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="newsroom-heading">
              Ixoraly Newsroom
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Access all the latest press releases and pertinent updates, available exclusively through Ixoraly.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-navy-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search press releases..."
                data-testid="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-transparent bg-white transition-all"
              />
            </div>

            {/* Category Filter */}
            <select
              data-testid="category-filter"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-transparent bg-white"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* Tag Filter */}
            <select
              data-testid="tag-filter"
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-transparent bg-white"
            >
              <option value="">All Tags</option>
              {tags.map((tag) => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>

          {(searchQuery || selectedCategory || selectedTag) && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {filteredReleases.length} of {pressReleases.length} press releases
              </p>
              <button
                data-testid="clear-filters-button"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('');
                  setSelectedTag('');
                }}
                className="text-sm text-navy-600 hover:text-navy-700 font-medium"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Press Releases Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredReleases.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="text-navy-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No press releases found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
              <Link
                to="/submit-press-release"
                className="inline-flex items-center px-6 py-3 bg-navy-600 text-white rounded-lg font-semibold hover:bg-navy-700 transition-colors"
              >
                Submit Your Press Release
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredReleases.map((release) => (
                <Link
                  key={release.id}
                  to={`/newsroom/${release.slug}`}
                  data-testid={`press-release-card-${release.id}`}
                  className="group bg-white rounded-xl overflow-hidden border-2 border-gray-100 hover:border-navy-200 hover:shadow-xl transition-all duration-300"
                >
                  {release.featuredImage && (
                    <div className="aspect-video overflow-hidden bg-gray-100">
                      <img
                        src={release.featuredImage}
                        alt={release.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    {release.categories.length > 0 && (
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 text-xs font-medium bg-navy-100 text-navy-700 rounded-full">
                          {release.categories[0]}
                        </span>
                      </div>
                    )}
                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-navy-600 transition-colors line-clamp-2">
                      {release.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">{stripHtml(release.excerpt)}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-1">
                        <Building2 size={14} />
                        <span>{release.company}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar size={14} />
                        <span>{new Date(release.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                    </div>
                    <div className="flex items-center text-navy-600 font-semibold text-sm mt-4 group-hover:gap-2 transition-all">
                      Read More
                      <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Have News to Share?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Get your press release distributed to thousands of media outlets and journalists worldwide.
          </p>
          <Link
            to="/submit-press-release"
            className="inline-flex items-center px-8 py-4 bg-navy-600 text-white rounded-lg font-semibold hover:bg-navy-700 transition-colors shadow-md"
          >
            Submit Your Press Release
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Newsroom;
